import React, { useEffect } from 'react';
import { useHubConnection, useDisconnectHub } from 'CustomHooks/useHubConnection';
import { IStyle } from 'Styles';
import { JobTypeDTO } from 'components/main/Setup/Imaging/ImageManagementTask/constants';
import NewProgressBar from 'components/main/Setup/Imaging/ImageManagementTask/Results/NewProgressBar';
import RMSvgIcon, {
  RMSvgIconNames,
} from 'components/customComponents/CommonComponents/RMSvgIcon';
import { useRMDispatch } from 'redux/hooks';
import {
  addBackgroundJob,
  setBackgroundJobs,
  updateBackgroundJobProgress,
  upsertBackgroundJob,
} from 'redux/reducers/backgroundJobsReducer';
import { dismissBackgroundJobThunk } from 'redux/thunks/backgroundJobs';
import { useGetBackgroundJobs } from 'redux/hooks/backgroundJobs';
import { EJobStatus } from 'business/models/CustomModels';
import { completeExperimentReportBackgroundJob } from 'components/reports/experimentReportBackgroundTask';

export interface BackgroundJobDTO {
  jobId: number | string;
  jobType: number;
  name: string;
  progress: number;
  logs: any[];
  dataObj: any;
  jobStatus?: number;
}

const CLOSE_ICON_FILL = '#DA1E28';
const TRACKED_JOB_TYPES = [JobTypeDTO.ImageManangement, JobTypeDTO.ExperimentReport];
const PERSIST_UNTIL_DISMISSED_JOB_TYPES = [JobTypeDTO.ExperimentReport];

const isTrackedJob = (jobType: number) => TRACKED_JOB_TYPES.includes(jobType);

const shouldPersistUntilDismissed = (jobType: number) =>
  PERSIST_UNTIL_DISMISSED_JOB_TYPES.includes(jobType);

const normalizeBackgroundJob = (job: BackgroundJobDTO): BackgroundJobDTO => ({
  ...job,
  jobId: job?.jobId,
  logs: job?.logs ?? [],
});

const getJobIcon = (jobType: number): RMSvgIconNames => {
  if (jobType === JobTypeDTO.ExperimentReport) {
    return 'ExperimentReport';
  }
  return 'ImageManagementTask';
};

/**
 * @todo This component is not a reusable/shared component. The component should not call services or global state, based on Wiki Code Web UI Standard.
 */
const BackgroundJobs = () => {
  const connection = useHubConnection();
  const disconnectHub = useDisconnectHub();
  const backgroundJobs = useGetBackgroundJobs();
  const dispatch = useRMDispatch();

  useEffect(() => {
    let isDisposed = false;
    let cleanupListeners: (() => void) | undefined;

    connection.start().then(() => {
      if (isDisposed) {
        return;
      }

      connection.invoke('RequestAllJobs').catch((error) => {
        console.error("Failed invoke 'RequestAllJobs',", error);
      });

      const handleAddNewJob = (data) => {
        if (data && isTrackedJob(data.jobType)) {
          dispatch(addBackgroundJob(normalizeBackgroundJob(data)));
        }
      };

      const handleReceiveAllJobs = (data) => {
        dispatch(
          setBackgroundJobs(
            data
              .filter((item) => isTrackedJob(item.jobType) && item.progress !== 100)
              .map(normalizeBackgroundJob),
          ),
        );
      };

      const handleUpdateJobProgress = async (data) => {
        if (!data || !isTrackedJob(data.jobType)) {
          return;
        }

        const job = normalizeBackgroundJob(data);

        if (
          job.jobType === JobTypeDTO.ExperimentReport &&
          job.progress === 100 &&
          (job.jobStatus === EJobStatus.Completed || job.jobStatus === EJobStatus.Error)
        ) {
          await completeExperimentReportBackgroundJob(job);
        }

        if (shouldPersistUntilDismissed(job.jobType)) {
          dispatch(upsertBackgroundJob(job));
          return;
        }

        dispatch(
          updateBackgroundJobProgress({
            jobId: job.jobId,
            progress: job.progress,
            name: job.name,
            jobType: job.jobType,
            jobStatus: job.jobStatus,
            dataObj: job.dataObj,
            logs: job.logs,
          }),
        );
      };

      connection.on('UpdateJobProgress', handleUpdateJobProgress);
      connection.on('AddNewJob', handleAddNewJob);
      connection.on('ReceiveAllJobs', handleReceiveAllJobs);

      cleanupListeners = () => {
        connection.off('UpdateJobProgress', handleUpdateJobProgress);
        connection.off('AddNewJob', handleAddNewJob);
        connection.off('ReceiveAllJobs', handleReceiveAllJobs);
      };
    });

    return () => {
      isDisposed = true;
      cleanupListeners?.();
      disconnectHub(connection);
    };
  }, []);

  return (
    <div id="background-jobs-wrapper">
      {backgroundJobs
        ?.filter(
          (job) =>
            !(
              Number(job.jobType) === JobTypeDTO.Archiving ||
              Number(job.jobType) === JobTypeDTO.Unarchive
            ),
        )
        .map((job) => (
          <div
            id="background-jobs-root"
            key={`background-job-${job.jobType}-${job.jobId}`}
            style={styles.cardRoot}
          >
            <div
              id="background-jobs-icon"
              style={styles.progress}
              className="flex flex-row justify-around"
            >
              <RMSvgIcon
                id={`background-jobs-icon-${job.jobId}`}
                onClick={() => {}}
                name={getJobIcon(job.jobType)}
                width={25}
                height={25}
              />
              <NewProgressBar
                mainContainerClassName="flex flex-column w-[85%]"
                title={job.name}
                progressBarContainerClass="w-[100%] taskProgress"
                currentProgress={job.progress}
                progressWidth={job.progress}
                progressContainerStyle={styles.progressBar}
                showProgressDigits={false}
              />
              {job.progress === 100 && (
                <button
                  id={`background-jobs-dismiss-${job.jobId}`}
                  type="button"
                  style={styles.iconButton}
                  aria-label="Dismiss notification"
                  onClick={() =>
                    dispatch(
                      dismissBackgroundJobThunk({ id: job.jobId, type: job.jobType }),
                    )
                  }
                >
                  <RMSvgIcon name="Close" fill={CLOSE_ICON_FILL} width={16} height={16} />
                </button>
              )}
            </div>
          </div>
        ))}
    </div>
  );
};

export default BackgroundJobs;
const styles: IStyle = {
  taskRow: {
    display: 'flex',
    flex: '100%',
  },
  taskTitle: {
    color: 'black',
  },
  cardRoot: {
    padding: '1.5%',
  },
  status: {
    fontSize: 'smaller',
    color: 'black',
  },
  progress: {
    fontSize: 'smaller',
    color: 'black',
    height: '1px !important',
  },
  clear: {
    marginTop: '1%',
  },
  iconButton: {
    background: 'none',
    border: 'none',
    color: 'black',
    cursor: 'pointer',
    fontSize: 'smaller',
    padding: 0,
  },
};
